import React, { useState } from "react";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getAuth,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import app from "../firebase.js";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ConnexionSMS() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [user, setUser] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const auth = getAuth(app);
  const db = getFirestore(app);
  const navigate = useNavigate();

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => console.log("✅ reCAPTCHA validé"),
      });
    }
    return window.recaptchaVerifier;
  };

  // 📱 Étape 1 : Envoi du code SMS avec vérification Firestore
  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 🔍 Vérifie si le numéro existe dans Firestore
      const q = query(collection(db, "users"), where("phone", "==", phone));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.error("❌ Aucun compte trouvé avec ce numéro. Veuillez d’abord vous inscrire.", {
          position: "top-right",
          autoClose: 3500,
        });
        setLoading(false);
        return;
      }

      // ✅ Si le numéro existe → envoi du SMS
      const appVerifier = setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(auth, phone, appVerifier);
      setConfirmationResult(confirmation);
      toast.info("📩 Code SMS envoyé ! Vérifiez votre téléphone.", {
        position: "top-right",
        autoClose: 3500,
      });
    } catch (error) {
      console.error("Erreur Firebase:", error);
      toast.error("⚠️ Erreur lors de l’envoi du SMS : " + error.message, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // 🔐 Étape 2 : Vérification du code reçu
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!confirmationResult) {
      toast.warn("📲 Demandez d’abord un code avant de vérifier.", {
        position: "top-right",
      });
      return;
    }

    setLoading(true);

    try {
      const result = await confirmationResult.confirm(code);
      setUser(result.user);
      toast.success("✅ Connexion réussie ! Bienvenue 🎉", {
        position: "top-right",
        autoClose: 2000,
      });

      // Redirige vers la page d’accueil après un court délai
      setTimeout(() => navigate("/"), 1500);
    } catch (error) {
      console.error(error);
      toast.error("❌ Code incorrect ou expiré.", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <ToastContainer />
      <div style={styles.card}>
        <button onClick={() => navigate("/connexion")} style={styles.backBtn}>
          ← Retour
        </button>

        <h2 style={styles.title}>Connexion par SMS 📲</h2>
        <p style={styles.subtitle}>
          Entrez votre numéro associé à un compte existant pour recevoir un code par SMS.
        </p>

        {!user ? (
          <>
            {!confirmationResult ? (
              <form onSubmit={handleSendCode} style={styles.form}>
                <input
                  type="tel"
                  placeholder="Entrez votre numéro (+33...)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={styles.input}
                  required
                />
                <button
                  type="submit"
                  style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
                  disabled={loading}
                >
                  {loading ? "Envoi..." : "Envoyer le code"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} style={styles.form}>
                <input
                  type="text"
                  placeholder="Entrez le code reçu"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  style={styles.input}
                  required
                />
                <button
                  type="submit"
                  style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
                  disabled={loading}
                >
                  {loading ? "Vérification..." : "Vérifier et se connecter"}
                </button>
              </form>
            )}
          </>
        ) : (
          <p style={styles.message}>🎉 Bienvenue ! Connexion réussie via SMS.</p>
        )}

        <div id="recaptcha-container" />
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #007bff, #00bfff)",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
    padding: "40px",
    textAlign: "center",
    width: "400px",
    position: "relative",
  },
  backBtn: {
    position: "absolute",
    top: "10px",
    left: "15px",
    background: "transparent",
    border: "none",
    color: "#007bff",
    fontSize: "18px",
    cursor: "pointer",
  },
  title: {
    color: "#007bff",
    marginBottom: "10px",
  },
  subtitle: {
    color: "#555",
    fontSize: "14px",
    marginBottom: "20px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  input: {
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "16px",
  },
  button: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "0.3s",
  },
  message: {
    color: "green",
    fontWeight: "bold",
    marginTop: "10px",
  },
};

export default ConnexionSMS;